import { Form, InputNumber, Popover, DatePicker, Switch } from 'antd'
import dayjs from 'dayjs'
import * as React from 'react'
import { useState } from 'react'
import { GraviButton, Horizontal, MaterialIcon, Texto } from '@gravitate-js/excalibrr'
import type { EditRateButtonProps } from '../types'
import { useRateManagement } from '../hooks'

/**
 * Component for editing an existing surcharge rate
 */
export function EditRateButton({ rate, surcharge, onSuccess }: EditRateButtonProps) {
  const [form] = Form.useForm<{
    rate: number
    effective_date: dayjs.Dayjs
    effective_to: dayjs.Dayjs | null
  }>()
  const [popoverVisible, setPopoverVisible] = useState(false)
  const [hasEndDate, setHasEndDate] = useState(false)

  const { isPercentageType, editRate, editRateMutation } = useRateManagement(surcharge, onSuccess)

  /**
   * Handle form submission
   */
  const handleEditRate = async (values: {
    rate: number
    effective_date: dayjs.Dayjs
    effective_to: dayjs.Dayjs | null
  }) => {
    const success = await editRate(rate, values, hasEndDate)
    if (success) {
      setPopoverVisible(false)
    }
  }

  /**
   * Render the edit rate form
   */
  const renderPopoverContent = () => (
    <Form
      form={form}
      layout='vertical'
      onFinish={handleEditRate}
      initialValues={{
        effective_date: dayjs(rate.effective_from),
        effective_to: rate.effective_to ? dayjs(rate.effective_to) : null,
        rate: isPercentageType ? rate.surcharge * 100 : rate.surcharge,
      }}
    >
      <Form.Item
        name='rate'
        label='Rate'
        rules={[
          { required: true, message: 'Please enter rate' },
          {
            validator: (_, value) => {
              if (isPercentageType && (value < 0 || value > 200)) {
                return Promise.reject('Percentage must be between 0 and 200')
              }
              return Promise.resolve()
            },
          },
        ]}
      >
        <InputNumber
          prefix={isPercentageType ? null : '$'}
          suffix={isPercentageType ? '%' : null}
          precision={5}
          min={0}
          max={isPercentageType ? 200 : undefined}
          style={{ width: '100%' }}
        />
      </Form.Item>

      <Form.Item label='Effective From' style={{ marginBottom: 8 }}>
        <Horizontal justifyContent='space-between' alignItems='flex-end' gap={8}>
          <Form.Item
            name='effective_date'
            rules={[{ required: true, message: 'Please select effective date' }]}
            style={{ marginBottom: 0 }}
          >
            <DatePicker format='MM/DD/YYYY' />
          </Form.Item>
        </Horizontal>
      </Form.Item>

      <Form.Item style={{ marginBottom: 16 }}>
        <Horizontal verticalCenter gap={8}>
          <Switch checked={hasEndDate} onChange={setHasEndDate} />
          <Texto>{hasEndDate ? 'End Date' : 'No End Date'}</Texto>
        </Horizontal>
      </Form.Item>

      {hasEndDate && (
        <Form.Item
          name='effective_to'
          label='Effective To'
          rules={[
            { required: hasEndDate, message: 'Please select end date' },
            {
              validator: (_, value) => {
                if (
                  hasEndDate &&
                  value &&
                  form.getFieldValue('effective_date') &&
                  value.isBefore(form.getFieldValue('effective_date'))
                ) {
                  return Promise.reject('End date must be after start date')
                }
                return Promise.resolve()
              },
            },
          ]}
          style={{ marginBottom: 16 }}
        >
          <DatePicker format='MM/DD/YYYY' />
        </Form.Item>
      )}

      <Horizontal gap={8} justifyContent='flex-end'>
        <GraviButton appearance='outlined' onClick={() => setPopoverVisible(false)}>
          Cancel
        </GraviButton>
        <GraviButton appearance='solid' theme2 htmlType='submit' loading={editRateMutation.isPending}>
          Save
        </GraviButton>
      </Horizontal>
    </Form>
  )

  return (
    <Popover
      content={renderPopoverContent()}
      title='Edit Rate'
      trigger='click'
      placement='bottomLeft'
      open={popoverVisible}
      onOpenChange={(visible) => {
        if (visible) {
          form.setFieldsValue({
            effective_date: dayjs(rate.effective_from),
            effective_to: rate.effective_to ? dayjs(rate.effective_to) : null,
            rate: isPercentageType ? rate.surcharge * 100 : rate.surcharge,
          })
          setHasEndDate(!!rate.effective_to)
        }
        setPopoverVisible(visible)
      }}
    >
      <GraviButton icon={<MaterialIcon className='pt-1' size={16} icon='edit' />} />
    </Popover>
  )
}
